import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useBlocker, useLocation, type Blocker, type Location } from 'react-router';
import { useConfirm } from '../components/dialog';

/**
 * Not losing somebody's typing when they leave the page.
 *
 * Every screen with a form reports whether it holds unsaved changes. While
 * anything does, ONE router blocker — React Router supports exactly one —
 * stops navigation to another page and asks first, in the app's own dialog:
 * a sidebar link, a search result in the command palette, the browser's Back
 * and Forward buttons, all of them. Reloading or closing the tab gets the
 * browser's own prompt instead, which is the only thing a page can do there.
 *
 * This replaced five separate guards. Each intercepted link clicks on the
 * document, because the app used a declarative router with no blocker, and so
 * each was blind to navigation made in code — the command palette — and to
 * the Back button, the two commonest ways a half-written change was lost.
 *
 * Changing only the query string (a tab, a filter) is not leaving the page and
 * is not asked about — unless the editor holding the changes is opened by that
 * part of the query. The catalogue's item editor lives at `?item=12` and a
 * person's at `?person=7`: Back from one changes nothing but the query, and
 * closes the editor as surely as going to another page. Such an editor names
 * its keys (`scope.params`), and a change to one of them is asked about too.
 */

export interface LeaveCopy {
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export interface LeaveScope {
  /** Query parameters that open this editor; changing one closes it. */
  params?: readonly string[];
}

interface Registration {
  copy?: LeaveCopy;
  params?: readonly string[];
}

interface LeaveGuardApi {
  /**
   * Records whether one editor holds unsaved changes, how to ask about them —
   * a record page says what will be lost in its own words — and which query
   * parameters open it. Takes effect at once.
   */
  report: (key: string, dirty: boolean, copy?: LeaveCopy, scope?: LeaveScope) => void;
  isDirty: () => boolean;
  /**
   * Asks when anything is unsaved and resolves to whether to go ahead. Saying
   * "discard" forgets every unsaved report, so the navigation that follows is
   * not asked about a second time.
   */
  confirmDiscard: (copy?: LeaveCopy) => Promise<boolean>;
}

const DEFAULT_COPY: Required<LeaveCopy> = {
  title: 'Discard unsaved changes?',
  description: 'You have changes here that have not been saved. If you leave, they are lost.',
  confirmLabel: 'Discard changes',
  cancelLabel: 'Keep editing',
};

/**
 * The editors a navigation would close: every one when the page changes;
 * otherwise those opened by a query parameter it changes.
 */
function closedBy(registrations: Map<string, Registration>, from: Location, to: Location): string[] {
  const keys = [...registrations.keys()];
  if (from.pathname !== to.pathname) return keys;
  const before = new URLSearchParams(from.search);
  const after = new URLSearchParams(to.search);
  return keys.filter((key) =>
    registrations.get(key)?.params?.some((param) => before.get(param) !== after.get(param)),
  );
}

const LeaveGuardContext = createContext<LeaveGuardApi | null>(null);

export function LeaveGuardProvider({ children }: { children: ReactNode }) {
  const confirm = useConfirm();
  // A ref, not state, so a report made just before a navigation is seen by it:
  // "save, then go to the list" must not be stopped by the work it just saved.
  const dirty = useRef(new Map<string, Registration>());
  const [count, setCount] = useState(0);

  const report = useCallback((key: string, isDirty: boolean, copy?: LeaveCopy, scope?: LeaveScope) => {
    const map = dirty.current;
    if (!isDirty) {
      if (!map.has(key)) return;
      map.delete(key);
    } else {
      // Re-inserting keeps the newest report last, so its wording is the one used.
      map.delete(key);
      map.set(key, { copy, params: scope?.params });
    }
    setCount(map.size);
  }, []);

  /** Asks about the given editors (all of them by default); forgets them on "discard". */
  const ask = useCallback(
    async (keys: string[], copy?: LeaveCopy) => {
      const map = dirty.current;
      const asked = keys.filter((key) => map.has(key));
      if (asked.length === 0) return true;
      const registered = map.get(asked[asked.length - 1]!)?.copy;
      const leave = await confirm({ ...DEFAULT_COPY, ...registered, ...copy, tone: 'danger' });
      if (leave) {
        for (const key of asked) map.delete(key);
        setCount(map.size);
      }
      return leave;
    },
    [confirm],
  );

  const confirmDiscard = useCallback((copy?: LeaveCopy) => ask([...dirty.current.keys()], copy), [ask]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => closedBy(dirty.current, currentLocation, nextLocation).length > 0,
  );
  const latest = useRef<Blocker>(blocker);
  latest.current = blocker;
  const location = useLocation();
  const here = useRef(location);
  here.current = location;
  const asking = useRef(false);

  useEffect(() => {
    if (blocker.state !== 'blocked' || asking.current) return;
    asking.current = true;
    // Only what this navigation closes: an editor it leaves open keeps its report.
    const closing = closedBy(dirty.current, here.current, blocker.location);
    void ask(closing).then((leave) => {
      asking.current = false;
      const current = latest.current;
      if (current.state !== 'blocked') return;
      if (leave) current.proceed();
      else current.reset();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the state is the trigger; the rest is read fresh
  }, [blocker.state, ask]);

  useEffect(() => {
    if (count === 0) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older browsers need a value here to show their prompt at all.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [count]);

  const api = useMemo<LeaveGuardApi>(
    () => ({ report, isDirty: () => dirty.current.size > 0, confirmDiscard }),
    [report, confirmDiscard],
  );

  return <LeaveGuardContext.Provider value={api}>{children}</LeaveGuardContext.Provider>;
}

const ALWAYS_LEAVE: LeaveGuardApi = {
  report: () => {},
  isDirty: () => false,
  confirmDiscard: async () => true,
};

/** The guard itself, for code that asks or checks without reporting. */
export function useLeaveGuardControls(): LeaveGuardApi {
  return useContext(LeaveGuardContext) ?? ALWAYS_LEAVE;
}

/**
 * Declares whether the calling form holds unsaved changes.
 *
 * `scope.params` names the query parameters that open the form, when it is
 * opened by one (`['item']` for `?item=12`), so that changing them asks too.
 *
 * `release()` withdraws the report at once — call it after a successful save
 * and before navigating away, or the save's own navigation would be asked
 * about. Unmounting withdraws it too.
 */
export function useLeaveGuard(
  dirty: boolean,
  copy?: LeaveCopy,
  scope?: LeaveScope,
): {
  confirmDiscard: (copy?: LeaveCopy) => Promise<boolean>;
  release: () => void;
} {
  const guard = useLeaveGuardControls();
  const key = useId();
  // The wording is read when asking, so a fresh object each render is harmless.
  const wording = useRef(copy);
  wording.current = copy;
  const params = scope?.params;
  const paramsKey = params?.join('&') ?? '';
  const scoped = useRef(params);
  scoped.current = params;

  useEffect(() => {
    guard.report(key, dirty, wording.current, { params: scoped.current });
  }, [guard, key, dirty, paramsKey]);
  useEffect(() => () => guard.report(key, false), [guard, key]);

  const release = useCallback(() => guard.report(key, false), [guard, key]);
  return { confirmDiscard: guard.confirmDiscard, release };
}
